<script setup lang="ts">
  import { computed, h, onMounted, onUnmounted, nextTick, ref, Ref, toRef, useSlots } from 'vue'
  import { ColumnDefinition } from './models/ColumnDefinition'
  import { ColumnsType, ColumnType } from 'ant-design-vue/es/table'
  import { Skeleton } from 'ant-design-vue'

  export interface ITableProps<TModel = any> {
    dataSource: Array<TModel>
    loading?: boolean
    columnDefinitions: Array<ColumnDefinition<TModel>>
    expandedRowKeys?: Array<string>
    paginationConfig?:
      | boolean
      | {
          showSizeChanger?: boolean
          pageSize?: number
          position?: ReadonlyArray<
            'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight'
          >
        }
    scroll?: { x?: boolean; y?: boolean }
    class?: any
    rowClassName?: (record: TModel) => string
    emitRowSelection?: boolean
    rowKey?: string | ((record: TModel) => string | number)
    showExpandToggle?: boolean
    expandToggleDisabled?: boolean
    allExpandableRowKeys?: string[]
  }

  const props = defineProps<ITableProps>()

  const rowKeyProp = computed(() => props.rowKey ?? 'id')

  type TModel = (typeof props)['dataSource'][number]

  const dataSource = toRef(props, 'dataSource') as Ref<TModel[]>
  const slots = useSlots()

  const footerRenderer = slots.footer ? () => slots.footer!() as any : undefined

  const isAllExpanded = computed(() => {
    if (props.expandToggleDisabled) return false
    const all = props.allExpandableRowKeys
    if (!all?.length) return false
    const expanded = new Set(props.expandedRowKeys ?? [])
    return all.every((k) => expanded.has(k))
  })

  function toggleExpandAll(checked: boolean) {
    if (checked) {
      emit('expand-all')
    } else {
      emit('expanded-rows-change', [])
    }
  }

  // treat a dataSource of skeleton rows (items with __skeleton === true) as "loading"
  const isSkeletonized = computed(() => {
    const ds = dataSource.value
    if (!ds || ds.length === 0) return false
    return ds.every((it: any) => it && it.__skeleton === true)
  })

  // ant-design-vue expects locale.emptyText to be a VNode/string (not a fn).
  // Only provide an emptyText when the dataSource is truly empty and not skeletonized and not loading.
  const emptyTextRenderer = computed(() => {
    if (!slots.emptyText) return undefined
    if (props.loading) return undefined
    if (isSkeletonized.value) return undefined
    if (!dataSource.value || dataSource.value.length === 0) {
      // call the slot now to get a VNode/string
      return slots.emptyText!() as any
    }
    return undefined
  })

  const emit = defineEmits<{
    'row-selected': [record: TModel, event: any]
    'expanded-rows-change': [keys: string[]]
    'expand-all': []
  }>()

  // helper to read record value by dataIndex (string | string[])
  const getRecordValue = (record: any, dataIndex: string | string[] | undefined) => {
    if (!dataIndex) return undefined
    if (Array.isArray(dataIndex))
      return dataIndex.reduce((acc, key) => (acc ? acc[key] : undefined), record)
    return record?.[dataIndex]
  }

  // Build ColumnsType<TModel> so each column's customRender checks:
  // 1) ColumnDefinition.cellRenderer
  // 2) ColumnDefinition.cellComponent
  // 3) parent-provided named slot (slotName or `cell-${key}`)
  // 4) fallback to dataIndex value
  const columns = computed(() => {
    return props.columnDefinitions.map((cd) => {
      // use the ColumnDefinition's toColumn so align/fixed/width are preserved
      const base = cd.toColumn()
      const key = base.key ?? cd.modelProperty
      const col: ColumnType<TModel> = {
        ...base,
        key
      } as ColumnType<TModel>

      // provide a single place to decide rendering priority
      col.customRender = (opt: {
        value: any
        text: any
        record: TModel
        index: number
        renderIndex: number
        column: ColumnType<TModel>
      }) => {
        // short-circuit skeleton rows: don't perform normal rendering for skeleton records
        if ((opt.record as any)?.__skeleton) {
          // return null for an empty cell; or return a Skeleton vnode if you prefer:
          if ((opt.record as any)?.__skeleton) {
            // use the Skeleton component and supply paragraph.rows > 0 so the shimmer bars are shown
            return h(Skeleton as any, {
              active: true,
              title: false,
              paragraph: { rows: 1, width: ['60%'] }
            })
          }
        }

        // 1) direct cellRenderer (ColumnDefinition)
        if (cd.cellRenderer) {
          const v = cd.cellRenderer(opt.record, opt.index)
          if (v !== undefined) return v
        }

        // 2) component supplied per-column
        if (cd.cellComponent) {
          // pass record/index/column plus any user-specified cellProps from ColumnDefinition
          return h(cd.cellComponent as any, {
            record: opt.record,
            index: opt.index,
            column: cd,
            ...(cd.cellProps ?? {}),
            ...(cd.clickable && props.emitRowSelection
              ? {
                  onClick: (e: MouseEvent) => {
                    emit('row-selected', opt.record, e)
                  },
                  style: { cursor: 'pointer' }
                }
              : {})
          })
        }

        // 3) parent slot named by slotName or default `cell-${key}`
        const slotName = cd.slotName ?? `cell-${key}`
        const slot = (slots as Record<string, Function | undefined>)[slotName]
        if (slot) {
          return slot({
            record: opt.record,
            index: opt.index,
            column: col,
            ...(cd.clickable && props.emitRowSelection
              ? {
                  onClick: (e: MouseEvent) => {
                    emit('row-selected', opt.record, e)
                  }
                }
              : {})
          })
        }

        // 4) fallback to simple dataIndex lookup
        const val = getRecordValue(opt.record, cd.columnId)
        return h(
          'span',
          {
            class: 'table-cell-default',
            ...(cd.clickable && props.emitRowSelection
              ? {
                  style: { cursor: 'pointer' },
                  onClick: (e: MouseEvent) => {
                    emit('row-selected', opt.record, e)
                  }
                }
              : {})
          },
          String(val ?? '')
        )
      }

      return col
    }) as ColumnsType<TModel>
  })

  const expandedRowsBinding = computed(() =>
    props.expandedRowKeys !== undefined ? { expandedRowKeys: props.expandedRowKeys } : {}
  )

  const tableWrapperRef = ref<HTMLElement | null>(null)
  const paginationVisible = ref(false)

  let paginationObserver: MutationObserver | null = null

  function checkPaginationVisible() {
    const el = tableWrapperRef.value
    if (!el) return
    paginationVisible.value = !!el.querySelector('.ant-table-pagination')
  }

  onMounted(() => {
    nextTick(() => {
      checkPaginationVisible()
      if (tableWrapperRef.value) {
        paginationObserver = new MutationObserver(checkPaginationVisible)
        paginationObserver.observe(tableWrapperRef.value, { childList: true, subtree: true })
      }
    })
  })

  onUnmounted(() => {
    paginationObserver?.disconnect()
  })
</script>

<template>
  <div ref="tableWrapperRef">
    <div
      v-if="showExpandToggle"
      :class="[
        'pointer-events-none relative z-[1] flex h-8 items-center gap-2',
        paginationVisible ? 'mb-[-30px]' : 'mb-2'
      ]"
    >
      <a-switch
        class="pointer-events-auto"
        :checked="isAllExpanded"
        :disabled="expandToggleDisabled"
        size="small"
        @change="(checked: boolean) => toggleExpandAll(checked)"
      />
      <span class="pointer-events-none select-none text-xs text-gray-500">Expand All</span>
    </div>
    <a-table
      :dataSource="dataSource"
      :rowKey="rowKeyProp"
      v-bind="expandedRowsBinding"
      @expandedRowsChange="(keys: string[]) => emit('expanded-rows-change', keys)"
      :columns="columns"
      :pagination="paginationConfig"
      :loading="loading"
      :scroll="scroll"
      :class="class"
      :rowClassName="rowClassName"
      :footer="footerRenderer"
      :locale="{ emptyText: emptyTextRenderer }"
    />
  </div>
</template>

<style scoped></style>

<style>
  [class*='ant-table-row'].row-delete-anim,
  [class*='ant-table-row'].row-delete-anim:hover,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-selected,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-level-0,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-level-1,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-selected:hover,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-selected,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-hover,
  [class*='ant-table-row'].row-delete-anim.ant-table-row-active {
    background-color: #ffeded !important;
    animation: rowDeletePulse 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 1;
  }
  [class*='ant-table-row'].matched-search {
    background-color: rgb(90, 167, 69, 0.5);
  }
  .dark [class*='ant-table-row'].matched-search {
    background-color: rgb(21, 34, 61, 0.6);
  }
  [class*='ant-table-row'].row-delete-anim::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ffeded;
    pointer-events: none;
    z-index: 10;
    border-radius: inherit;
    animation: rowDeletePulse 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes rowDeletePulse {
    0% {
      background-color: #ffeded;
      opacity: 1;
      transform: scale(1);
    }
    80% {
      background-color: #ffb3b3;
      opacity: 0.7;
      transform: scale(0.98);
    }
    100% {
      background-color: #fff;
      opacity: 0.3;
      transform: scale(0.95);
    }
  }

  .ant-table-content > table {
    margin-top: 5px;
    padding: 3px;
  }

  .ant-table-wrapper .ant-table-pagination.ant-pagination.ant-table-pagination-right:first-of-type,
  .ant-table-wrapper .ant-table-pagination.ant-pagination.ant-table-pagination-left:first-of-type {
    margin-top: 0px;
    margin-bottom: 5px;
  }

  .ant-table-wrapper .ant-table-pagination.ant-pagination.ant-table-pagination-right,
  .ant-table-wrapper .ant-table-pagination.ant-pagination.ant-table-pagination-left {
    margin-top: 15px;
    margin-bottom: 5px;
  }
</style>
